import { useCallback, useSyncExternalStore } from 'react'
import { api } from '../services/API'
import type { SubscricaoModulo, TenantSettings } from '../utils/types'

/**
 * Módulos que a subscrição activa do tenant permite — fonte única, partilhada
 * por toda a app (Sidebar, CondominiumLayout, Modules…). Um só pedido a
 * `/api/tenant/me` é feito e o resultado é distribuído por um store externo;
 * quem monta depois reaproveita a cache.
 *
 * Semântica de `temModulo`:
 * - lista carregada  → só os módulos nela (sem plano o backend devolve `[]`).
 * - a carregar / erro → devolve `true` (fail-open, para não esconder o menu
 *   num soluço de rede — a API continua a ser a autoridade e responde 403).
 */

interface Snapshot {
    loading: boolean
    /** `null` enquanto carrega ou se o pedido falhou. */
    subscricaoActiva: boolean | null
    /** `null` enquanto carrega ou se o pedido falhou. */
    modulos: ReadonlySet<SubscricaoModulo> | null
}

const LOADING: Snapshot = { loading: true, subscricaoActiva: null, modulos: null }

let snapshot: Snapshot = LOADING
let inFlight: Promise<void> | null = null
const listeners = new Set<() => void>()

function emit() {
    for (const l of listeners) l()
}

function fetchModulos(): Promise<void> {
    if (inFlight) return inFlight
    inFlight = api
        .get<TenantSettings>('/api/tenant/me')
        .then((r) => {
            snapshot = {
                loading: false,
                subscricaoActiva: r.data.subscricaoActiva ?? null,
                modulos: new Set(r.data.modulosDisponiveis ?? []),
            }
        })
        .catch(() => {
            snapshot = { loading: false, subscricaoActiva: null, modulos: null }
        })
        .finally(() => {
            inFlight = null
            emit()
        })
    return inFlight
}

/**
 * Descarta a cache — usar no logout para o próximo login não herdar os módulos
 * do tenant anterior. Só volta a pedir se ainda houver componentes montados a
 * ouvir; caso contrário o próximo `subscribe` (a seguir ao login) trata disso.
 */
export function invalidateTenantModulos() {
    snapshot = LOADING
    inFlight = null
    emit()
    if (listeners.size > 0) fetchModulos()
}

function subscribe(cb: () => void) {
    listeners.add(cb)
    if (snapshot.loading && !inFlight) fetchModulos()
    return () => {
        listeners.delete(cb)
    }
}

export function useTenantModulos() {
    const state = useSyncExternalStore(subscribe, () => snapshot, () => snapshot)
    const temModulo = useCallback(
        (m: SubscricaoModulo) => (state.modulos ? state.modulos.has(m) : true),
        [state.modulos],
    )
    return { ...state, temModulo }
}
